import { serviceKnownErrors, serviceTriageErrors } from './api-nest.errors';
import {
  buildBaseQuery,
  buildUnknownLogFilter,
} from './datadog-query-builders';
import {
  extensionKnownErrors,
  extensionTriageErrors,
} from './extension.errors';
import { supportingServiceErrors } from './services.errors';
import type {
  DeployedServiceType,
  Environment,
  LogBasedError,
  MonitorContext,
} from './types';
import { webViteKnownErrors, webViteTriageErrors } from './web-vite.errors';

/** The production environments unknown-error monitoring spans. */
export const PRODUCTION_ENVIRONMENTS = [
  'production',
  'production-siloam',
] as const;

export type UnknownErrorService = {
  service: DeployedServiceType;
  context: MonitorContext;
  errors: LogBasedError[];
};

/**
 * Canonical roster of services with unknown-error monitoring, each paired
 * with its monitor context and full known/triage error list. Single source
 * for every consumer that enumerates the unclassified-error surface — the
 * unknown-error digest targets (eluve-team-ops) and the log-based metrics
 * (pulumi-core) — so the catalog cannot drift between them.
 */
export const UNKNOWN_ERROR_SERVICES: UnknownErrorService[] = [
  {
    service: 'api-nest',
    context: 'service',
    errors: [...serviceTriageErrors, ...serviceKnownErrors],
  },
  ...Array.from(supportingServiceErrors.entries()).map(
    ([service, errors]): UnknownErrorService => ({
      service,
      context: 'service',
      errors,
    }),
  ),
  {
    service: 'web-vite',
    context: 'application',
    errors: [...webViteTriageErrors, ...webViteKnownErrors],
  },
  {
    service: 'eluve-extension',
    context: 'extension',
    errors: [...extensionTriageErrors, ...extensionKnownErrors],
  },
];

/**
 * Full unclassified-error log query (base scope minus the exclusion list)
 * for one service in one environment — the same semantics the unknown-error
 * monitors alert on.
 */
export const buildUnknownErrorQuery = (
  svc: UnknownErrorService,
  options: { environment: Environment; projectId?: string },
): string =>
  `${buildBaseQuery({
    context: svc.context,
    environment: options.environment,
    projectId: options.projectId,
    service: svc.service,
    status: 'error',
  })} ${buildUnknownLogFilter(svc.context, svc.errors)}`;
